import { useMemo, useState } from 'react';

import type { App, AppWavePin } from '@keboola/api-client/developerPortal/types';
import { Button, Modal, Skeleton } from '@keboola/design';

import { QueryError } from '../../../components/QueryError';
import { newestFirst } from '../../apps/versions';
import { useAdminAppVersions } from '../queries';
import { pinnableVersions, pinOf } from '../waves';

import { WavePinsFields } from './WavePinsFields';

/**
 * The body and footer of the wave pins modal: loads the versions, then shows the form.
 *
 * - The pins are frozen when the dialog opens, so a refetch cannot move the releaser guard's baseline.
 * - Offers only versions built from the app's current image, newest first.
 */
export function WavePinsForm({ app, onSaved }: { app: App; onSaved: () => void }) {
  const [openedWith] = useState(() => app.waveTags);
  const versions = useAdminAppVersions(app.id);
  const candidates = useMemo(
    (): AppWavePin[] =>
      newestFirst(pinnableVersions(versions.data ?? [], app.repository?.uri ?? null))
        .map(pinOf)
        .filter((pin) => pin !== null),
    [versions.data, app.repository?.uri],
  );

  if (versions.data !== undefined) {
    return (
      <WavePinsFields
        appId={app.id}
        openedWith={openedWith}
        candidates={candidates}
        onSaved={onSaved}
      />
    );
  }

  return (
    <>
      <Modal.Body>
        {versions.isLoading && <Skeleton className="tw:h-40" />}
        <QueryError
          isError={versions.isError}
          hasData={false}
          error={versions.error}
          onRetry={() => void versions.refetch()}
          loadTitle="Could not load the versions"
          refreshTitle="Could not refresh the versions"
          staleNote="Showing the versions from the last successful load."
          titleAs="h3"
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Close asChild>
          <Button type="button" variant="secondary">
            Cancel
          </Button>
        </Modal.Close>
      </Modal.Footer>
    </>
  );
}
